import React from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { isRoomOwnedByUser } from "@lifeat/shared/rooms/RoomUtil";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import BannedIcon from "@icons/BannedIcon.svg";
import VolumeMutedIcon from "@icons/VolumeMutedIcon.svg";
import { FontSize } from "@themes/Fonts";
import UserAvatar from "@user/components/UserAvatar/UserAvatar";
import RoomMemberDisplayName from "../../RoomMemberDisplayName";
import { ModerationState } from "../../RoomMemberTypes";
import { RoomParams } from "../../RoomTypes";
import { PublicUserInfoForRoom } from "../../room-chat/RoomChatTypes";
import RoomModerationActionMenu from "../../room-chat/components/RoomModerationActionMenu";

const UserContainer = styled.div`
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
    padding: 7px 5px;
    position: relative;
`;

const UserDisplay = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
`;

const IndicatorContainer = styled.div`
    display: flex;
    align-items: center;
`;

const IndicatorText = styled.div`
    color: ${(props) => props.theme.textColorSpecial};
    font-size: ${FontSize.mini};
    margin-left: 2px;
`;

const StyledMuteIcon = styled(VolumeMutedIcon)`
    fill: ${(props) => props.theme.textColorSpecial};
`;

const StyledBannedIcon = styled(BannedIcon)`
    fill: ${(props) => props.theme.textColorSpecial};
`;

interface RoomMemberProps {
    readonly userDisplayInfo: PublicUserInfoForRoom | undefined;
    readonly modState?: ModerationState;
}
export function RoomMember(props: RoomMemberProps) {
    const { roomId }: RoomParams = useParams();
    const { userData }: UserContextType = useUserContext();

    const isHost = isRoomOwnedByUser(roomId, userData);

    let indicatorText;
    let indicatorIcon;

    if (props.modState === "banned") {
        indicatorText = "banned";
        indicatorIcon = <StyledBannedIcon />;
    } else if (props.modState === "muted") {
        indicatorText = "muted";
        indicatorIcon = <StyledMuteIcon />;
    }

    const userStateIndicator = indicatorText && (
        <IndicatorContainer>
            {indicatorIcon}
            <IndicatorText>{indicatorText}</IndicatorText>
        </IndicatorContainer>
    );

    return (
        <UserContainer>
            <UserDisplay>
                <UserAvatar
                    size={"small"}
                    userDisplayName={props.userDisplayInfo?.userDisplayName ?? "User"}
                    userAviUrl={props.userDisplayInfo?.userAviUrl ?? ""}
                />
                <RoomMemberDisplayName
                    displayName={props.userDisplayInfo?.userDisplayName}
                    isDisplayNameBold={false}
                    isHost={!!props.userDisplayInfo?.isHost}
                />
                {userStateIndicator}
            </UserDisplay>
            {props.userDisplayInfo && (
                <RoomModerationActionMenu
                    roomId={roomId}
                    targetUid={props.userDisplayInfo.uid}
                    isHost={isHost}
                    isVisible={true}
                />
            )}
        </UserContainer>
    );
}
